// Holiday cache keyed by year — avoids redundant network calls within a session.
const holidayCache = {};

async function fetchUSHolidays(year) {
  if (holidayCache[year]) return holidayCache[year];

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 5000);

  try {
    const res = await fetch(
      `https://date.nager.at/api/v3/PublicHolidays/${year}/US`,
      { signal: controller.signal }
    );
    if (!res.ok) throw new Error("non-200");
    const data = await res.json();
    const set = new Set(data.map((h) => h.date)); // "YYYY-MM-DD"
    holidayCache[year] = set;
    return set;
  } catch {
    // On failure cache an empty set so subsequent calls don't retry immediately,
    // but the session-level cache means a page refresh will retry.
    holidayCache[year] = new Set();
    return new Set();
  } finally {
    clearTimeout(timer);
  }
}

// Counts business days strictly AFTER startDate up to and including today.
// Example: hire on Friday → Mon=1, Tue=2, Wed=3 → "> 3" fires on Thursday.
function countBusinessDaysInRange(startDate, endDate, holidays) {
  let count = 0;
  const d = new Date(startDate);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 1); // start counting from the day AFTER hire date

  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  while (d <= end) {
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) {
      const iso = d.toISOString().slice(0, 10);
      if (!holidays.has(iso)) count++;
    }
    d.setDate(d.getDate() + 1);
  }
  return count;
}

/**
 * Returns the number of U.S. business days (Mon–Fri, excl. federal holidays)
 * that have elapsed strictly after `startDate` through today (inclusive).
 *
 * Uses the Nager.Date public API (HTTPS, no key required).
 * Falls back gracefully to weekday-only counting if the API is unavailable.
 */
export async function countBusinessDaysSince(startDate) {
  const start = new Date(startDate);
  if (isNaN(start.getTime())) return 0;

  const today = new Date();
  if (start >= today) return 0;

  const startYear = start.getFullYear();
  const endYear = today.getFullYear();
  const years = [];
  for (let y = startYear; y <= endYear; y++) years.push(y);

  const sets = await Promise.all(years.map(fetchUSHolidays));
  const holidays = new Set();
  sets.forEach((s) => s.forEach((d) => holidays.add(d)));

  return countBusinessDaysInRange(start, today, holidays);
}
